import { catalogFeatures, catalogImports } from "./catalogs/manifest";
import type { CatalogFeature } from "./catalogs/manifest";
import type { LocaleCode, TranslationDictionary, TranslationPattern } from "./types";

export { catalogFeatures };
export type { CatalogFeature };

type Catalog = { translations: TranslationDictionary; patterns: TranslationPattern[] };
const catalogs = new Map<LocaleCode, Catalog>();
const completed = new Set<string>();
const pending = new Map<string, Promise<void>>();

export const getLoadedCatalog = (locale: LocaleCode): Catalog | undefined => catalogs.get(locale);

export { routeCatalogFeatures } from "./catalogRoutes";

export const catalogsReady = (locale: LocaleCode, features: readonly CatalogFeature[]): boolean =>
  locale === "en" || ["common", "patterns", ...features].every((feature) => completed.has(`${locale}:${feature}`));

export const loadLocaleCatalogs = async (locale: LocaleCode, features: readonly CatalogFeature[]): Promise<void> => {
  if (locale === "en") return;
  const load = (feature: CatalogFeature | "patterns") => {
    const key = `${locale}:${feature}`;
    if (completed.has(key)) return Promise.resolve();
    const existing = pending.get(key);
    if (existing) return existing;
    const flight = catalogImports[locale][feature]().then((module) => {
      const catalog = catalogs.get(locale) || { translations: {}, patterns: [] };
      if (feature === "patterns") catalog.patterns = module.default as TranslationPattern[];
      else Object.assign(catalog.translations, module.default);
      catalogs.set(locale, catalog);
      completed.add(key);
    }).finally(() => pending.delete(key));
    pending.set(key, flight);
    return flight;
  };
  await Promise.all([load("common"), load("patterns"), ...features.map(load)]);
};

export const loadLocaleCatalogsWithRetry = async (locale: LocaleCode, features: readonly CatalogFeature[]): Promise<void> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await loadLocaleCatalogs(locale, features);
      return;
    } catch (error) {
      if (attempt >= 2) throw error;
      await new Promise((resolve) => window.setTimeout(resolve, attempt === 0 ? 250 : 1000));
    }
  }
};

// Used by test setup only. The application never imports the canonical all-locale catalogs.
export const registerCompleteCatalog = (locale: LocaleCode, catalog: Catalog): void => {
  catalogs.set(locale, catalog);
  [...catalogFeatures, "patterns"].forEach((feature) => completed.add(`${locale}:${feature}`));
};

export const clearCatalogCacheForTests = (): void => {
  catalogs.clear();
  completed.clear();
  pending.clear();
};
